import cn from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import {
  ONBOARDING_NAVIGATE_START_DELAY_MS as NAVIGATE_START_DELAY_MS,
  patientAgentMessages,
} from '../config';
import { OnboardingFlow, parseOnboardingFlow } from '../lib';

import styles from './Onboarding.module.scss';

type OnboardingFlowCardProps = {
  /** Raw fenced-block body for language `onboarding`. */
  code: string;
  /** Live-run callback: starts the tour over the real page. */
  onStart?: (flow: OnboardingFlow) => void;
};

/** Renders the raw fenced body when it fails validation — the agent's
 * guidance must never silently vanish. */
const OnboardingFlowFallback: FC<{ code: string }> = ({ code }) => (
  <pre className={styles.fallbackCode}>{code}</pre>
);

/**
 * Inline chat card for a valid ```onboarding block. Invalid bodies render as
 * a plain code block — this is a targeting language, not a styling channel,
 * so the agent's payload must stay visible even when validation rejects it.
 */
export const OnboardingFlowCard: FC<OnboardingFlowCardProps> = ({
  code,
  onStart,
}) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [askNavigate, setAskNavigate] = useState(false);
  const navigateStartTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(navigateStartTimer.current), []);
  const flow = useMemo(() => parseOnboardingFlow(code), [code]);
  if (!flow) {
    return <OnboardingFlowFallback code={code} />;
  }

  const unavailable = onStart === undefined;
  // The flow is page-bound: a path recorded at creation time that differs
  // from the current page means the tour would find no targets here.
  const otherPage = Boolean(flow.pagePath) && flow.pagePath !== pathname;

  return (
    <div className={styles.card}>
      <div className={styles.cardBody}>
        <strong className={styles.cardTitle}>
          {flow.title ||
            formatMessage(patientAgentMessages.onboardingFallbackTitle)}
        </strong>
        <span className={cn('p3', styles.cardMeta)}>
          {formatMessage(patientAgentMessages.onboardingStepsCount, {
            count: flow.steps.length,
          })}
        </span>
      </div>
      {otherPage && askNavigate ? (
        <div className={styles.cardNavigateAsk}>
          <span className={cn('p3', styles.cardNavigateText)}>
            {formatMessage(patientAgentMessages.onboardingOtherPageQuestion)}
          </span>
          <div className={styles.cardNavigateActions}>
            <button
              type="button"
              className={styles.cardButton}
              onClick={() => {
                setAskNavigate(false);
                navigate(flow.pagePath as string);
                // If the user has navigated somewhere else again by the time
                // the delay elapses, do not start the tour over a foreign
                // page — the card stays as the entry point.
                navigateStartTimer.current = window.setTimeout(() => {
                  if (flow.pagePath === window.location.pathname) {
                    onStart?.(flow);
                  }
                }, NAVIGATE_START_DELAY_MS);
              }}
            >
              {formatMessage(patientAgentMessages.onboardingOtherPageGo)}
            </button>
            <button
              type="button"
              className={cn(styles.cardButton, styles.cardButtonDisabled)}
              onClick={() => setAskNavigate(false)}
            >
              {formatMessage(patientAgentMessages.onboardingOtherPageCancel)}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={cn(
            styles.cardButton,
            unavailable && styles.cardButtonDisabled,
          )}
          disabled={unavailable}
          onClick={() => {
            if (otherPage) {
              setAskNavigate(true);
              return;
            }
            onStart?.(flow);
          }}
        >
          {formatMessage(patientAgentMessages.onboardingShowMe)}
        </button>
      )}
    </div>
  );
};

/**
 * The Streamdown custom renderer for the `onboarding` fence language. While
 * the block is still streaming, nothing renders yet (the JSON is incomplete);
 * once complete, invalid bodies fall back to the default code block.
 */
export const makeOnboardingRenderer =
  (onStart?: (flow: OnboardingFlow) => void) =>
  ({ code, isIncomplete }: { code: string; isIncomplete: boolean }) => {
    if (isIncomplete) {
      return null;
    }
    return <OnboardingFlowCard code={code} onStart={onStart} />;
  };
